import {
    faChevronLeft,
    faRotateLeft,
    faTrash,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Button,
    ButtonGroup,
    Slider,
    NumberInput,
    Skeleton,
    DatePicker,
    Tab,
    Tabs,
} from "@heroui/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Key } from "@react-types/shared";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { WaterRiskZones, SoilMoistureCorrection } from "@/types/water";
import dayjs from "dayjs";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { getLocalTimeZone, parseDate } from "@internationalized/date";

export const Route = createFileRoute(
    "/dashboard/fields/$fieldId/insights/hydroflow/settings",
)({
    component: RouteComponent,
});

type UnitMode = "fc" | "aw";

function RouteComponent() {
    const { t } = useTranslation("insights");
    const { fieldId, fieldQuery, fieldWaterQuery } = useFieldDashboard() ?? {};
    const { formatUnit } = useI18n();
    const { useGetRequest, useMutateRequest } = useRequest();

    const waterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
    });

    const currentDateDefaults = useMemo(() => {
        const today = getTodayWithTimeTravel();
        const data = waterContentQuery.data?.data?.find((d) =>
            dayjs(d.time).isSame(today, "day"),
        );
        if (!data) return { lowerBound: null, upperBound: null };
        const lowerBound = Math.round((data.rtsml_b / data.fhc) * 100);
        const upperBound = Math.round((data.rtsml_t / data.fhc) * 100);
        return {
            fieldCapacity: data.fhc,
            wiltingPoint: data.wp,
            lowerBound: lowerBound ?? null,
            upperBound: upperBound ?? null,
            soilMoisture: data.swc_est,
        };
    }, [waterContentQuery.data?.data]);

    const correctionsQuery = useGetRequest<SoilMoistureCorrection[]>({
        url: `fields/${fieldId}/soil_moisture_corrections/`,
    });

    const correctionsData = useMemo(() => {
        return (
            correctionsQuery.data?.map((d) => ({
                date: dayjs(d.date).format("YYYY-MM-DD"),
                observed_soil_moisture: d.observed_soil_moisture,
            })) ?? []
        );
    }, [correctionsQuery.data]);

    const createCorrection = useMutateRequest({
        url: `fields/${fieldId}/soil_moisture_corrections/`,
        method: "post",
        successMessage: t("messages.updatedInsightsSettings"),
        errorMessage: t("messages.failedToUpdateSettings"),
        onSuccess: () => correctionsQuery.refetch(),
    });

    const deleteCorrection = useMutateRequest({
        method: "delete",
        errorMessage: t("messages.failedToUpdateSettings"),
        onSuccess: () => correctionsQuery.refetch(),
    });

    const [newDate, setNewDate] = useState(
        parseDate(dayjs().format("YYYY-MM-DD")),
    );
    const [newMoisture, setNewMoisture] = useState<number | undefined>(
        currentDateDefaults.soilMoisture ?? undefined,
    );

    const handleAddCorrection = useCallback(() => {
        if (!newDate || newMoisture == null) return;
        createCorrection.mutate({
            data: { date: dayjs(newDate.toDate(getLocalTimeZone())).format("YYYY-MM-DD"), observed_soil_moisture: newMoisture },
        });
    }, [newDate, newMoisture, createCorrection]);

    const patchField = useMutateRequest({
        url: `fields/${fieldId}/`,
        method: "patch",
        successMessage: t("messages.updatedInsightsSettings"),
        errorMessage: t("messages.failedToUpdateSettings"),
        onSuccess: () => {
            fieldQuery?.refetch();
            waterContentQuery.refetch();
            fieldWaterQuery?.refetch();
        },
    });

    const patchFieldRef = useRef(patchField);
    patchFieldRef.current = patchField;

    const debouncedPatch = useMemo(
        () =>
            debounce((data: Record<string, unknown>) => {
                patchFieldRef.current.mutate({ data });
            }, 600),
        [],
    );

    useEffect(() => () => debouncedPatch.cancel(), [debouncedPatch]);

    const [waterInsightsActive, setWaterInsightsActive] = useState("auto");
    const [unitMode, setUnitMode] = useState<UnitMode>("fc");
    const [fieldCapacity, setFieldCapacity] = useState<number | undefined>(
        currentDateDefaults.fieldCapacity ?? undefined,
    );
    const [wiltingPoint, setWiltingPoint] = useState<number | undefined>(
        currentDateDefaults.wiltingPoint ?? undefined,
    );
    const availableWater = useMemo(() => {
        return fieldCapacity && wiltingPoint
            ? fieldCapacity - wiltingPoint
            : undefined;
    }, [fieldCapacity, wiltingPoint]);
    const [optimalMin, setOptimalMin] = useState<number | undefined>(
        currentDateDefaults.lowerBound ?? undefined,
    );
    const [optimalMax, setOptimalMax] = useState<number | undefined>(
        currentDateDefaults.upperBound ?? undefined,
    );

    const valueUnit = unitMode === "fc" ? formatUnit("in", "mm") : "VWC %";

    useEffect(() => {
        if (fieldQuery?.data) {
            const waterInsights =
                fieldQuery.data.water_insights_active_manual == null
                    ? "auto"
                    : fieldQuery.data.water_insights_active_manual
                        ? "active"
                        : "inactive";
            setWaterInsightsActive(waterInsights);
            setFieldCapacity(
                fieldQuery.data.water_holding_capacity ??
                currentDateDefaults.fieldCapacity ??
                undefined,
            );
            setWiltingPoint(
                fieldQuery.data.wilting_point ??
                currentDateDefaults.wiltingPoint ??
                undefined,
            );
            setOptimalMin(
                fieldQuery.data.target_moisture_min ??
                currentDateDefaults.lowerBound ??
                undefined,
            );
            setOptimalMax(
                fieldQuery.data.target_moisture_max ??
                currentDateDefaults.upperBound ??
                undefined,
            );
            setNewMoisture(currentDateDefaults.soilMoisture ?? undefined);
        }
    }, [fieldQuery?.data, currentDateDefaults]);

    const onFieldCapacityChange = useCallback(
        (value: number) => {
            setFieldCapacity(value);
            debouncedPatch({ water_holding_capacity: value });
        },
        [debouncedPatch],
    );

    const onWiltingPointChange = useCallback(
        (value: number) => {
            setWiltingPoint(value);
            debouncedPatch({ wilting_point: value });
        },
        [debouncedPatch],
    );

    const onAvailableWaterChange = useCallback(
        (value: number) => {
            const fc = value + (wiltingPoint ?? 0);
            setFieldCapacity(fc);
            debouncedPatch({ water_holding_capacity: fc });
        },
        [debouncedPatch, wiltingPoint],
    );

    const onWaterInsightsChange = useCallback(
        (val: Key) => {
            setWaterInsightsActive(val as string);
            const water_insights_active_manual =
                val === "auto" ? null : val === "active";
            patchField.mutate({
                data: { water_insights_active_manual },
            });
        },
        [patchField],
    );

    return (
        <div className="flex flex-col gap-4 p-2 justify-center">
            <div className="flex gap-2 items-center">
                <Button
                    size="sm"
                    color="primary"
                    isIconOnly
                    as={Link}
                    to={`/dashboard/fields/${fieldId}/insights/hydroflow`}
                    aria-label="Back to HydroFlow"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <div className="text-xl font-semibold">HydroFlow Settings</div>
            </div>

            <div>
                <div className="font-semibold">
                    {t("settings.waterInsightsActive")}
                </div>
                <Tabs
                    selectedKey={waterInsightsActive}
                    onSelectionChange={onWaterInsightsChange}
                >
                    <Tab key="auto" title={t("settings.auto")} />
                    <Tab key="active" title={t("settings.active")} />
                    <Tab key="inactive" title={t("settings.inactive")} />
                </Tabs>
            </div>

            <div className="flex items-center justify-between gap-2 rounded-lg bg-default-100 px-3 py-2 text-sm text-default-600">
                <span>Values that haven't been updated are estimated.</span>
                <Button
                    size="sm"
                    variant="flat"
                    startContent={<FontAwesomeIcon icon={faRotateLeft} />}
                    onPress={() => {
                        patchField.mutate({
                            data: {
                                water_holding_capacity: null,
                                wilting_point: null,
                                target_moisture_min: null,
                                target_moisture_max: null,
                            },
                        });
                    }}
                >
                    Reset to estimated
                </Button>
            </div>

            <Skeleton isLoaded={!patchField.isPending} className="rounded-lg">
                <div className="flex flex-row gap-4 max-w-full">
                    <ButtonGroup size="sm">
                        <Button
                            color={unitMode === "fc" ? "primary" : "default"}
                            onPress={() => setUnitMode("fc")}
                        >
                            FC
                        </Button>
                        <Button
                            color={unitMode === "aw" ? "primary" : "default"}
                            onPress={() => setUnitMode("aw")}
                        >
                            Available Water
                        </Button>
                    </ButtonGroup>

                    <label className="text-sm font-semibold">
                        {unitMode === "fc"
                            ? "Field Capacity"
                            : "Available Water"}
                        {unitMode === "fc" ? (
                            <NumberInput
                                value={fieldCapacity}
                                onValueChange={onFieldCapacityChange}
                                size="sm"
                                min={0}
                                endContent={<span>{valueUnit}</span>}
                            />
                        ) : (
                            <NumberInput
                                value={availableWater}
                                onValueChange={onAvailableWaterChange}
                                size="sm"
                                min={0}
                                endContent={<span>{valueUnit}</span>}
                            />
                        )}
                    </label>
                    <label className="text-sm font-semibold">
                        Wilting Point
                        <div className="mt-1">
                            <NumberInput
                                value={wiltingPoint}
                                onValueChange={onWiltingPointChange}
                                size="sm"
                                min={0}
                                endContent={formatUnit("in", "mm")}
                            />
                        </div>
                    </label>
                </div>
            </Skeleton>

            <Skeleton isLoaded={!patchField.isPending} className="rounded-lg">
                <div className="max-w-full">
                    <Slider
                        label="Optimal Range (% FC)"
                        minValue={0}
                        maxValue={100}
                        step={1}
                        showTooltip
                        value={[optimalMin ?? 0, optimalMax ?? 100]}
                        onChange={(v) => {
                            const [min, max] = v as number[];
                            setOptimalMin(min);
                            setOptimalMax(max);
                            debouncedPatch({
                                target_moisture_min: min,
                                target_moisture_max: max,
                            });
                        }}
                        className="text-sm font-semibold"
                    />
                    <div className="flex gap-2 mt-2">
                        <label className="text-sm font-semibold flex-1">
                            Min
                            <NumberInput
                                value={optimalMin}
                                onValueChange={(v) => {
                                    const clamped = Math.min(
                                        v,
                                        optimalMax ?? 100,
                                    );
                                    setOptimalMin(clamped);
                                    debouncedPatch({
                                        target_moisture_min: clamped,
                                    });
                                }}
                                size="sm"
                                min={0}
                                max={optimalMax ?? 100}
                                endContent={<span>%</span>}
                            />
                        </label>
                        <label className="text-sm font-semibold flex-1">
                            Max
                            <NumberInput
                                value={optimalMax}
                                onValueChange={(v) => {
                                    const clamped = Math.max(
                                        v,
                                        optimalMin ?? 0,
                                    );
                                    setOptimalMax(clamped);
                                    debouncedPatch({
                                        target_moisture_max: clamped,
                                    });
                                }}
                                size="sm"
                                min={optimalMin ?? 0}
                                max={100}
                                endContent={<span>%</span>}
                            />
                        </label>
                    </div>
                </div>
            </Skeleton>

            <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold">Observations</div>
                <div className="flex gap-2 items-end">
                    <DatePicker
                        label="Date"
                        labelPlacement="outside"
                        size="sm"
                        value={newDate}
                        onChange={(e) =>
                            setNewDate(
                                e ?? parseDate(dayjs().format("YYYY-MM-DD")),
                            )
                        }
                    />
                    <NumberInput
                        label="Soil Moisture"
                        labelPlacement="outside"
                        size="sm"
                        value={newMoisture}
                        onValueChange={setNewMoisture}
                        min={0}
                        endContent={<span>{formatUnit("in", "mm")}</span>}
                    />
                    <Button
                        size="sm"
                        color="primary"
                        isLoading={createCorrection.isPending}
                        onPress={handleAddCorrection}
                        isDisabled={newMoisture == null}
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add
                    </Button>
                </div>
                {correctionsData.length > 0 && (
                    <table className="w-full text-xs border-collapse">
                        <thead>
                            <tr className="bg-default-100">
                                <th className="p-1 border border-default-200 text-left font-semibold">
                                    Date
                                </th>
                                <th className="p-1 border border-default-200 text-left font-semibold">
                                    Soil Moisture ({formatUnit("in", "mm")})
                                </th>
                                <th className="p-1 border border-default-200 w-8" />
                            </tr>
                        </thead>
                        <tbody>
                            {correctionsData.map((c) => (
                                <tr key={c.date}>
                                    <td className="p-1 border border-default-200">
                                        {c.date}
                                    </td>
                                    <td className="p-1 border border-default-200">
                                        {c.observed_soil_moisture}
                                    </td>
                                    <td className="p-1 border border-default-200 text-center">
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            color="danger"
                                            variant="flat"
                                            isLoading={
                                                deleteCorrection.isPending
                                            }
                                            onPress={() =>
                                                deleteCorrection.mutate({
                                                    url: `fields/${fieldId}/soil_moisture_corrections/`,
                                                    params: {
                                                        date: c.date,
                                                    },
                                                })
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
